import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { passwordSchema } from "@shared/utils/yupSchema";
import { CustomTextField } from "@shared/components/CustomTextField";
import { supabase, updatePassword } from "@services/supabaseAPI";
import { requireStepUp } from "@shared/utils/stepUpManager";
import { useNotification } from "@shared/contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import AnnogramImage from "@/shared/assets/Annogram.png";
import PasswordStrengthMeter from "@shared/components/PasswordStrengthMeter";

interface Password {
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordScreen = () => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Password>({
    resolver: yupResolver(passwordSchema),
  });
  // Live-update the strength meter as the user types. See SignUpForm
  // for the same pattern + rationale.
  const passwordValue = watch("newPassword") ?? "";

  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const submitPassword = async (data: Password) => {
    // The recovery link lands the user in an AAL1 session. Supabase
    // refuses password updates at AAL1 for anyone with an enrolled MFA
    // factor ("AAL2 session is required to update email or password
    // when MFA is enabled"), so pre-flight the step-up exactly as the
    // in-app SecurityPanel does. No-op for users without a factor.
    if (!(await requireStepUp())) return;

    setSubmitting(true);
    // Race the SDK's updateUser promise against the USER_UPDATED event,
    // mirroring SecurityPanel: after the step-up rotates the session to
    // AAL2, the Supabase JS updateUser promise can hang even though the
    // change lands server-side. The event fires from inside the SDK's
    // response handling and is a reliable settled signal. No wall-clock
    // timeout, deliberately — a false success would navigate away with
    // the PUT still in flight and the SDK's session lock held.
    let unsubscribe = () => {};
    const userUpdatedFromEvent = new Promise<{
      success: true;
      error: string;
    }>((resolve) => {
      const { data: sub } = supabase.auth.onAuthStateChange((event) => {
        if (event === "USER_UPDATED") resolve({ success: true, error: "" });
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });
    // updatePassword always returns a truthy object — the previous
    // `if (response)` branch silently treated server errors as success
    // and navigated away regardless. Branch on response.success.
    let response: { success: boolean; error?: string };
    try {
      response = await Promise.race([
        updatePassword(data.newPassword),
        userUpdatedFromEvent,
      ]);
    } finally {
      unsubscribe();
      setSubmitting(false);
    }

    if (response.success) {
      showSuccess("Password changed successfully.");
      reset();
      navigate("/workspaces");
      return;
    }

    showError(
      response.error || "Could not update password. Please try again."
    );
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6 },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(submitPassword)}
        noValidate
        aria-labelledby="reset-password-heading"
        sx={{ width: "100%" }}
      >
        <Stack spacing={3} alignItems="stretch">
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={AnnogramImage}
              alt="Annogram"
              sx={{ width: { xs: "55%", sm: "45%" }, maxWidth: 240 }}
            />
          </Box>
          <Typography
            id="reset-password-heading"
            component="h1"
            variant="h5"
            textAlign="center"
          >
            Choose a new password
          </Typography>
          <Stack spacing={2}>
            <CustomTextField
              {...register("newPassword")}
              autoFocus
              autoComplete="new-password"
              name="newPassword"
              label="New password"
              type="password"
              disabled={submitting}
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message ?? " "}
              fullWidth
            />
            <PasswordStrengthMeter password={passwordValue} />
            <CustomTextField
              {...register("confirmPassword")}
              autoComplete="new-password"
              name="confirmPassword"
              label="Confirm new password"
              type="password"
              disabled={submitting}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message ?? " "}
              fullWidth
            />
          </Stack>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting}
            startIcon={
              submitting ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {submitting ? "Updating…" : "Update password"}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default ResetPasswordScreen;
